// eslint-disable-next-line no-unused-vars
import React from 'react';
import { bool, func, object } from 'prop-types';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import {
  ArrowDown,
  ArrowUp,
  CircleCheck,
  Info,
  Package
} from 'lucide-react';

import Img from 'Containers/Image/Image';
import PromptModal from 'Containers/PromptModal/PromptModal';
import Badges from 'Components/Badges/Badges';
import useNumberUtils from 'Hooks/useNumberUtils';

const StyledContent = styled.div`
  color: ${ props => props.theme.colors.darkGray };

  .intro {
    align-items: center;
    background: #f2faf4;
    border: 1px solid #b8ddbf;
    display: flex;
    gap: 1rem;
    justify-content: space-between;
    margin-bottom: 1rem;
    padding: 1rem;

    h4 {
      font-size: 1.25rem;
      font-weight: 700;
      margin: 0 0 0.35rem;
    }

    .intro-icon {
      color: ${ props => props.theme.colors.green };
      flex: 0 0 auto;
    }
  }

  .updated-at {
    color: ${ props => props.theme.colors.gray };
    font-size: 0.8rem;
    margin: 0;
  }

  .summary {
    margin: 1rem 0;
  }

  .summary-card {
    border: 1px solid ${ props => props.theme.products.colors.border };
    border-left: 4px solid ${ props => props.theme.colors.blue };
    border-radius: 0;
    padding: 0.875rem;

    p {
      align-items: baseline;
      display: flex;
      gap: 0.5rem;
    }

    strong {
      color: ${ props => props.theme.colors.darkGray };
      font-size: 1.5rem;
    }
  }

  .table-wrap {
    border: 1px solid ${ props => props.theme.products.colors.border };
    border-radius: 6px;
    margin-top: 1rem;
    max-height: 380px;
    overflow: auto;
  }

  table {
    border-collapse: collapse;
    min-width: 980px;
    table-layout: fixed;
    width: 100%;
  }

  th:first-of-type,
  td:first-of-type {
    width: 30%;
  }

  th {
    background: #f5f7f9;
    color: #3a3a3a;
    font-size: 0.875rem;
    font-weight: 500;
    padding: 0.75rem;
    text-align: left;
  }

  .header-label {
    background: #edf8f5;
    border: 1px solid #73b5a5;
    border-radius: 999px;
    color: #236658;
    display: block;
    font-size: 0.6rem;
    margin-top: 0.25rem;
    padding: 0.1rem 0.35rem;
    width: max-content;
  }

  td {
    border-top: 1px solid ${ props => props.theme.products.colors.border };
    padding: 0.75rem;
    vertical-align: middle;
  }

  .product {
    align-items: center;
    display: grid;
    gap: 0.75rem;
    grid-template-columns: 54px minmax(0, 1fr);
    min-width: 260px;
  }

  .preview {
    align-items: center;
    border: 1px solid ${ props => props.theme.products.colors.border };
    display: flex;
    height: 54px;
    justify-content: center;
    overflow: hidden;
    padding: 3px;
    width: 54px;

    .image {
      max-height: 100%;
      max-width: 100%;
    }
  }

  .title {
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    display: -webkit-box;
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 1.2rem;
    overflow: hidden;
  }

  .sku {
    color: ${ props => props.theme.colors.darkGray };
    font-size: 0.8rem;
    margin-top: 0.25rem;

    .sku-label {
      font-weight: 600;
    }

    .sku-value {
      font-weight: normal;
      margin-left: 5px;
    }
  }

  .product .badges {
    justify-content: flex-start;
    margin: 2px 0 0;

    .badge {
      font-size: 12px;
      font-weight: 600;
      min-height: auto;
    }
  }

  .price-difference {
    align-items: center;
    display: inline-flex;
    gap: 0.35rem;

    &.increase {
      color: ${ props => props.theme.colors.red };
    }

    &.decrease {
      color: ${ props => props.theme.colors.green };
    }
  }

  .price-change-percent {
    display: flex;
    font-weight: 700;
    margin-top: 0.25rem;
    width: max-content;
  }

  .impact-value {
    font-weight: 700;
  }

  .notice {
    align-items: flex-start;
    border-radius: 6px;
    display: flex;
    gap: 0.75rem;
    margin-top: 1rem;
    padding: 0.875rem;

    &.info {
      background: #edf3ff;
      border: 1px solid #9bbcff;
      color: ${ props => props.theme.colors.blue };
    }

  }

  .totals {
    display: flex;
    justify-content: flex-end;
    margin: 1.5rem 0 0;

    > div {
      border-left: 1px solid ${ props => props.theme.products.colors.border };
      min-width: 185px;
      padding: 0.75rem 1rem;
      text-align: right;
    }

    .grand-total {
      background: #edf3ff;
      color: ${ props => props.theme.colors.blue };

      strong {
        color: ${ props => props.theme.colors.darkGray };
        font-size: 1.25rem;
      }
    }

    small {
      color: ${ props => props.theme.colors.gray };
      display: block;
      font-size: 0.875rem;
      margin-bottom: 0.35rem;
    }

    strong {
      color: ${ props => props.theme.colors.darkGray };
      display: block;
      font-size: 1.125rem;
    }
  }
`;

const getValue = change => Number(change?.new ?? 0);
const getOldValue = change => Number(change?.old ?? 0);
const getDifference = change => Number(change?.difference ?? 0);
const isChanged = change => !!change?.isChanged || getDifference(change) !== 0;

const OfferPriceChangesModal = ({
  open,
  changes,
  onClose
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { formatNumber, formatDate } = useNumberUtils();
  const currency = String(changes?.currencyId || 'ron').toUpperCase();
  const formatMoney = value => formatNumber(value, {
    currency,
    maximumFractionDigits: 2
  });
  const items = (changes?.items || []).filter(item => (
    item.isPriceChanged
    || isChanged(item.unitResellerPrice)
    || isChanged(item.unitOfferPrice)
  ));
  const totalDifference = getDifference(changes?.totals?.totalWithoutVat);
  const hasTotalIncrease = totalDifference > 0;

  const body = (
    <StyledContent theme={ theme }>
      <div className="intro">
        <div>
          <h4>{ t('offers.priceChangesSectionTitle') }</h4>
          <p className="updated-at">
            { t('offers.priceChangesUpdatedAt', {
              date: formatDate(changes?.checkedAt)
            }) }
          </p>
        </div>
        <CircleCheck className="intro-icon" size={ 44 } strokeWidth={ 1.75 } />
      </div>

      <section className="summary">
        <div className="summary-card">
          <p>
            <strong>{ items.length }</strong>
            { t('offers.priceChangesModifiedProducts') }
          </p>
        </div>
      </section>

      { items.length ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{ t('offers.product') }</th>
                <th>
                  { t('offers.priceChangesOldNodPrice') }
                  <span className="header-label">{ t('offers.priceChangesUnitPrice') }</span>
                </th>
                <th>
                  { t('offers.priceChangesNewNodPrice') }
                  <span className="header-label">{ t('offers.priceChangesUnitPrice') }</span>
                </th>
                <th>
                  { t('offers.priceChangesOldOfferPrice') }
                  <span className="header-label">{ t('offers.priceChangesUnitPrice') }</span>
                </th>
                <th>
                  { t('offers.priceChangesNewOfferPrice') }
                  <span className="header-label">{ t('offers.priceChangesUnitPrice') }</span>
                </th>
                <th>{ t('offers.priceChangesImpact') }</th>
              </tr>
            </thead>
            <tbody>
              { items.map(item => {
                const nodDifference = getDifference(item.unitResellerPrice);
                const impact = getDifference(item.totalWithoutVat);
                const impactIncrease = impact > 0;

                return (
                  <tr key={ item.productId || item.id }>
                    <td>
                      <div className="product">
                        <div className="preview">
                          <Img
                            className="image"
                            src={ item.pictureUrl }
                            unloader={ <Package size={ 28 } strokeWidth={ 1.25 } /> }
                            alt={ item.title || item.name || '' }
                          />
                        </div>
                        <div>
                          <p className="title">{ item.title || item.name }</p>
                          <p className="sku">
                            <span className="sku-label">{ t('products.code') }:</span>
                            <span className="sku-value">
                              { item.code || item.erpProductId || '-' }
                            </span>
                          </p>
                          { !!item.supplierId && <Badges isSupplierOffer /> }
                        </div>
                      </div>
                    </td>
                    <td>{ formatMoney(getOldValue(item.unitResellerPrice)) }</td>
                    <td>
                      <strong>{ formatMoney(getValue(item.unitResellerPrice)) }</strong>
                      { isChanged(item.unitResellerPrice) && (
                        <span
                          className={ `price-difference price-change-percent ${
                            nodDifference > 0 ? 'increase' : 'decrease'
                          }` }
                        >
                          { formatNumber(item.unitResellerPrice.differencePercent || 0, {
                            maximumFractionDigits: 2
                          }) }%
                          { nodDifference > 0
                            ? <ArrowUp size={ 15 } />
                            : <ArrowDown size={ 15 } /> }
                        </span>
                      ) }
                    </td>
                    <td>{ formatMoney(getOldValue(item.unitOfferPrice)) }</td>
                    <td><strong>{ formatMoney(getValue(item.unitOfferPrice)) }</strong></td>
                    <td>
                      <span
                        className={ impact === 0
                          ? 'impact-value'
                          : `impact-value price-difference ${
                            impactIncrease ? 'increase' : 'decrease'
                          }` }
                      >
                        { impact > 0 ? '+' : '' }
                        { formatMoney(impact) }
                      </span>
                    </td>
                  </tr>
                );
              }) }
            </tbody>
          </table>
        </div>
      ) : (
        <p>{ t('offers.priceChangesNoProductChanges') }</p>
      ) }

      <div className="notice info">
        <Info size={ 20 } />
        <p>{ t('offers.priceChangesMarkupNotice') }</p>
      </div>
      <div className="totals">
        <div>
          <small>{ t('offers.priceChangesOldTotal') }</small>
          <strong>{ formatMoney(getOldValue(changes?.totals?.totalWithoutVat)) }</strong>
        </div>
        <div className="grand-total">
          <small>{ t('offers.priceChangesNewTotal') }</small>
          <strong>{ formatMoney(getValue(changes?.totals?.totalWithoutVat)) }</strong>
        </div>
        <div>
          <small>{ t('offers.priceChangesDifference') }</small>
          <strong className={ totalDifference > 0 ? 'price-difference increase' : 'price-difference decrease' }>
            { hasTotalIncrease ? '+' : '' }
            { formatMoney(totalDifference) }
          </strong>
        </div>
      </div>
    </StyledContent>
  );

  return (
    <PromptModal
      modalOpen={ open }
      className="OfferPriceChangesModal"
      size="md"
      onCloseModal={ onClose }
      onConfirm={ () => {} }
      header={ t('offers.priceChangesTitle') }
      body={ body }
      confirmButton={ t('offers.priceChangesUnderstood') }
      showCancelButton={ false }
    />
  );
};

OfferPriceChangesModal.propTypes = {
  open: bool,
  changes: object,
  onClose: func
};

OfferPriceChangesModal.displayName = 'OfferPriceChangesModal';

export default OfferPriceChangesModal;
